import { useState, useEffect, useRef, useCallback } from "react";
import AudioEngine from "../audio/AudioEngine";
import { DEFAULT_PARAMS } from "../constants/presets";

// Bidirectional mapping between repeats (1-20) and feedback (0-0.95)
export const repeatsToFeedback = (r) => {
  if (r <= 1) return 0;
  return Math.min(0.95, Math.max(0, 0.28 * Math.log(r)));
};

export const feedbackToRepeats = (f) => {
  if (f <= 0) return 1;
  return Math.min(20, Math.max(1, Math.round(Math.exp(f / 0.28))));
};

export const useAudioEngine = () => {
  const engineRef = useRef(null);
  const [isSupported, setIsSupported] = useState(true);
  const [micState, setMicState] = useState("disconnected"); // disconnected | connecting | connected | error | denied
  const [errorMessage, setErrorMessage] = useState("");
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [levels, setLevels] = useState({ inputLevel: 0, outputLevel: 0 });
  
  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordResult, setRecordResult] = useState(null); // { blob, url, format }

  // Backing Track states
  const [musicLoaded, setMusicLoaded] = useState(false);
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [musicName, setMusicName] = useState("");
  const [musicProgress, setMusicProgress] = useState({ currentTime: 0, duration: 0 });

  // Check browser support
  useEffect(() => {
    setIsSupported(AudioEngine.isSupported());
  }, []);

  // Initialize engine reference
  if (!engineRef.current && isSupported) {
    engineRef.current = new AudioEngine();
  }

  // Active loop for meters and visualizer frame request
  const animationFrameId = useRef(null);
  const startMetricsLoop = useCallback(() => {
    const updateMetrics = () => {
      if (engineRef.current && micState === "connected") {
        const currentLevels = engineRef.current.getInstantLevels();
        setLevels(currentLevels);
        animationFrameId.current = requestAnimationFrame(updateMetrics);
      }
    };
    updateMetrics();
  }, [micState]);

  const stopMetricsLoop = useCallback(() => {
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }
    setLevels({ inputLevel: 0, outputLevel: 0 });
  }, []);

  // Sync metrics loop with mic connection
  useEffect(() => {
    if (micState === "connected") {
      startMetricsLoop();
    } else {
      stopMetricsLoop();
    }
    return () => stopMetricsLoop();
  }, [micState, startMetricsLoop, stopMetricsLoop]);

  // Clean up engine on unmount
  useEffect(() => {
    return () => {
      if (engineRef.current) {
        engineRef.current.destroy();
      }
    };
  }, []);



  // Request mic access and connect
  const startMic = async () => {
    if (!isSupported) {
      setMicState("error");
      setErrorMessage("Web Audio API / Microphone capture is not supported in this browser.");
      return;
    }

    setMicState("connecting");
    setErrorMessage("");
    setRecordResult(null);

    try {
      // Request direct raw audio stream for zero-latency monitoring without software buffer delays
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,   // Eliminates speaker acoustic feedback & beeping
          noiseSuppression: true,   // Keeps background noise & feedback tones suppressed
          autoGainControl: false,   // Disabled to prevent automatic gain ducking that cuts off words
          latency: { ideal: 0.01 },
          channelCount: 1,
        },
        video: false,
      });

      // Start the audio engine
      await engineRef.current.start(stream);
      
      // Apply existing UI parameters to the new audio nodes
      engineRef.current.applyPreset(params);
      
      setMicState("connected");
    } catch (err) {
      console.error("Microphone access failed:", err);
      setMicState("error");
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        setMicState("denied");
        setErrorMessage("Microphone access was denied. Please update browser settings and try again.");
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        setErrorMessage("No microphone device was found on this system.");
      } else {
        setErrorMessage(`Microphone access failed: ${err.message || "Unknown error"}`);
      }
    }
  };

  // Stop microphone and disconnect nodes
  const stopMic = () => {
    if (engineRef.current) {
      engineRef.current.stop();
    }
    setMicState("disconnected");
    setIsRecording(false);
    setMusicPlaying(false);
    setMusicProgress({ currentTime: 0, duration: 0 });
  };

  // Update specific parameters
  const updateParam = useCallback((key, value) => {
    setParams((prev) => {
      const next = { ...prev, [key]: value };
      
      // Handle bidirectional syncing of feedback & repeats
      if (key === "feedback") {
        next.repeats = feedbackToRepeats(value);
      } else if (key === "repeats") {
        next.feedback = repeatsToFeedback(value);
      }

      // Propagate parameters to audio engine
      if (engineRef.current) {
        if (key === "musicVolume") {
          engineRef.current.setMusicVolume(next.musicVolume);
        } else if (micState === "connected") {
          const engine = engineRef.current;
          switch (key) {
            case "isMuted":
              engine.setMute(next.isMuted);
              break;
            case "delayTime":
              engine.setDelayTime(next.delayTime);
              break;
            case "feedback":
              engine.setFeedback(next.feedback);
              break;
            case "repeats":
              engine.setFeedback(next.feedback);
              break;
            case "echoVolume":
              engine.setEchoVolume(next.echoVolume);
              break;
            case "dryWetMix":
              engine.setDryWetMix(next.dryWetMix);
              break;
            case "masterVolume":
              engine.setMasterGain(next.masterVolume);
              break;
            case "stereoPan":
              engine.setStereoPan(next.stereoPan);
              break;
            case "noiseReduction":
              engine.setNoiseReduction(next.noiseReduction);
              break;
            case "compressor":
              engine.setCompressor(next.compressor);
              break;
            case "reverb":
              engine.setReverb(next.reverb);
              break;
            case "reverbVolume":
              engine.setReverbVolume(next.reverbVolume);
              break;
            case "eqLow":
              engine.setEqLow(next.eqLow);
              break;
            case "eqMid":
              engine.setEqMid(next.eqMid);
              break;
            case "eqHigh":
              engine.setEqHigh(next.eqHigh);
              break;
            default:
              break;
          }
        }
      }
      
      return next;
    });
  }, [micState]);

  // Load a complete preset
  const loadPreset = useCallback((preset) => {
    setParams((prev) => {
      const next = {
        ...prev,
        ...preset,
      };

      if (engineRef.current && micState === "connected") {
        engineRef.current.applyPreset(next);
      }

      return next;
    });
  }, [micState]);

  // Start recording output
  const startRecording = () => {
    if (engineRef.current && micState === "connected") {
      const success = engineRef.current.startRecording();
      if (success) {
        setIsRecording(true);
        setRecordResult(null);
      }
    }
  };

  // Stop recording output
  const stopRecording = async () => {
    if (engineRef.current && isRecording) {
      const result = await engineRef.current.stopRecording();
      setIsRecording(false);
      if (result) {
        setRecordResult(result);
      }
    }
  };

  // Clear compiled recording result state
  const clearRecordResult = useCallback(() => {
    setRecordResult(null);
  }, []);

  // Backing Track Controllers
  const loadMusicTrack = useCallback((file) => {
    if (engineRef.current) {
      setMusicName(file.name);
      engineRef.current.loadMusic(file, (state) => {
        if (state.playing !== undefined) setMusicPlaying(state.playing);
        if (state.currentTime !== undefined) {
          setMusicProgress({
            currentTime: state.currentTime,
            duration: state.duration,
          });
        }
      });
      setMusicLoaded(true);
    }
  }, []);

  const playMusicTrack = useCallback(() => {
    if (engineRef.current && musicLoaded) {
      engineRef.current.playMusic();
    }
  }, [musicLoaded]);

  const pauseMusicTrack = useCallback(() => {
    if (engineRef.current && musicLoaded) {
      engineRef.current.pauseMusic();
    }
  }, [musicLoaded]);

  const seekMusicTrack = useCallback((time) => {
    if (engineRef.current && musicLoaded) {
      engineRef.current.seekMusic(time);
    }
  }, [musicLoaded]);

  // Expose the analyser object directly so Visualizer components can read high frequency frames
  const getAnalyserData = useCallback(() => {
    if (engineRef.current) {
      return engineRef.current.getAnalyserData();
    }
    return null;
  }, []);

  return {
    isSupported,
    micState,
    errorMessage,
    params,
    levels,
    isRecording,
    recordResult,
    musicLoaded,
    musicPlaying,
    musicName,
    musicProgress,
    startMic,
    stopMic,
    updateParam,
    loadPreset,
    startRecording,
    stopRecording,
    clearRecordResult,
    getAnalyserData,
    loadMusicTrack,
    playMusicTrack,
    pauseMusicTrack,
    seekMusicTrack,
  };
};
export default useAudioEngine;
